/**
 * poll-display — how a poll reads on a staff surface (staff-lists wave,
 * Lane C, 2026-09-17).
 *
 * One table for the status words and tones, shared by the Polls list row and
 * the detail page's badge, so "blocked" never prints as "Blocked" on one
 * screen and "Couldn't open" on another. Tones are NAMES for `StatusChip`
 * (design-grammar §3), never class strings.
 *
 * The phrases are the ones the UI/UX guideline §7 fixes: a close date is
 * written relative to the poll's stage ("Closes" · "Closed" · "Would close"),
 * in the house short date; the Answered cell is a dash before anything was
 * sent (decision 13a) and keeps B-417's null ≠ zero once it was.
 */
import type { ChipTone } from '@/components/editorial';
import type { Poll, PollResultsResponse, PollStatus } from '@/types/polls';
import { trfEventDate, trfEventOrCalendarDay } from './trfTime';

export interface PollStatusChipSpec {
  label: string;
  tone: ChipTone;
}

/** Every status the model knows (backend/src/models/Poll.js POLL_STATUS), in lifecycle order. */
export const POLL_STATUS_ORDER: PollStatus[] = ['draft', 'scheduled', 'open', 'closed', 'blocked'];

const STATUS_CHIP: Record<PollStatus, PollStatusChipSpec> = {
  draft: { label: 'Not sent yet', tone: 'warn' },
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  open: { label: 'Open', tone: 'ok' },
  closed: { label: 'Closed', tone: 'neutral' },
  // The audience no longer passed scope when the scheduled open ran
  // (Poll.js: "never a silent send, never a silent non-send").
  blocked: { label: 'Couldn’t open — audience changed', tone: 'danger' },
};

export function pollStatusChip(status: PollStatus): PollStatusChipSpec {
  return STATUS_CHIP[status] ?? { label: String(status), tone: 'neutral' };
}

/** The Status filter's options, in plain words. */
export const POLL_STATUS_FILTERS: Array<{ value: PollStatus; label: string }> = POLL_STATUS_ORDER.map(
  (value) => ({ value, label: STATUS_CHIP[value].label }),
);

/** Statuses under which nothing has reached a family yet. */
const UNSENT = new Set<PollStatus>(['draft', 'scheduled', 'blocked']);

/**
 * "Closes Sep 30, 2026" while open · "Closed Sep 3, 2026" once closed
 * (`closed_at`, else the planned `end_at`) · "Would close Sep 30, 2026" while
 * nothing has been sent. A dash when the server sent no usable date.
 */
export function pollCloseLabel(poll: Pick<Poll, 'status' | 'end_at' | 'closed_at'>): string {
  if (poll.status === 'closed') {
    // #2756 (#2704 class): the viewer's day of the instant, not the UTC day.
    const when = poll.closed_at ? trfEventDate(poll.closed_at) : trfEventOrCalendarDay(poll.end_at);
    return when ? `Closed ${when}` : '—';
  }
  const when = trfEventOrCalendarDay(poll.end_at);
  if (!when) return '—';
  return UNSENT.has(poll.status) ? `Would close ${when}` : `Closes ${when}`;
}

/**
 * The Answered cell. "—" before the poll was sent — the snapshot is taken at
 * publish, so any number before then is a guess (§1.7). Once sent, "5 of 20";
 * when the publish-time snapshot failed, "5 · reach unknown" (B-417: null is
 * an unknown reach, not a reach of zero).
 */
export function pollAnsweredLabel(poll: Pick<Poll, 'status' | 'response_count' | 'audience_size'>): string {
  if (UNSENT.has(poll.status)) return '—';
  if (poll.audience_size === null || poll.audience_size === undefined) return `${poll.response_count} · reach unknown`;
  return `${poll.response_count} of ${poll.audience_size}`;
}

export type PollOrigin = 'quick' | 'form';

export function pollOrigin(poll: Pick<Poll, 'form_definition_id'>): PollOrigin {
  return poll.form_definition_id ? 'form' : 'quick';
}

/** "From a form" on a definition-minted send; "Quick poll" otherwise. */
export function pollOriginLabel(poll: Pick<Poll, 'form_definition_id'>): string {
  return pollOrigin(poll) === 'form' ? 'From a form' : 'Quick poll';
}

export const POLL_ORIGIN_LABELS: Record<PollOrigin, string> = {
  quick: 'Quick polls',
  form: 'Sent from a form',
};

export interface PollListFilters {
  q?: string;
  status?: PollStatus | '';
  anon?: boolean;
  origin?: PollOrigin | '';
}

/**
 * Is this send a FORM send — a check-in or a custom form minted from a
 * definition — rather than a quick poll? (2026-10-23, `(?) checkin-custom-on-today`.)
 *
 * The family app splits on this: a form send is a task row on Today, a quick
 * poll is a card in the Community feed. It reads `form_definition_id`, not
 * `form_kind`: the server sends `form_kind: null` when it could not read the
 * definition, and a check-in must not fall into the Community feed because a
 * label lookup failed. Shared by `CommunityPage` and `DashboardPage` so the
 * two can never both show a send, or both drop it.
 */
export function isFormSend(poll: { form_definition_id?: string | null }): boolean {
  return Boolean(poll.form_definition_id);
}

/**
 * Who sent a row of the team's list (`(?) polls-team-edit`): "Ana Ruiz", or
 * "You" on the caller's own row when no name could be read, else "A teammate".
 */
export function pollCreatorLabel(poll: Pick<Poll, 'created_by_name' | 'can_manage'>): string {
  if (poll.created_by_name) return poll.created_by_name;
  return poll.can_manage ? 'You' : 'A teammate';
}

/**
 * The #456 floor, explained on the results view (2026-10-23). The number is
 * the server's (`min_responses_required`, from MIN_ANONYMOUS_RESULTS_THRESHOLD
 * in polls.js) — never a second hard-coded 5 here. Null when the results are
 * not suppressed, or the server named no floor.
 */
export function anonymityFloorLine(
  results: Pick<PollResultsResponse, 'results_suppressed' | 'min_responses_required'>,
): string | null {
  if (!results.results_suppressed || results.min_responses_required == null) return null;
  return `Results appear once ${results.min_responses_required} families have answered, so no one’s answer can be identified.`;
}

/** Client-side cut over the loaded page; the order is the API's (newest first). */
export function filterPolls(polls: Poll[], filters: PollListFilters): Poll[] {
  const needle = (filters.q ?? '').trim().toLowerCase();
  return polls.filter((p) => {
    if (needle && !p.title.toLowerCase().includes(needle)) return false;
    if (filters.status && p.status !== filters.status) return false;
    if (filters.anon && !p.anonymous) return false;
    if (filters.origin && pollOrigin(p) !== filters.origin) return false;
    return true;
  });
}
